import { useMemo, useState, type FormEvent } from 'react';
import { discussions, type DiscussComment } from '@/mocks/topics';
import { formatCount } from '@/utils/format';

interface DiscussionSectionProps {
  topicId: number;
  topicTitle: string;
}

const MAX_LENGTH = 200;

export default function DiscussionSection({ topicId, topicTitle }: DiscussionSectionProps) {
  const initial = useMemo(
    () => discussions.filter((item) => item.topicId === topicId),
    [topicId],
  );
  const [comments, setComments] = useState<DiscussComment[]>(initial);
  const [likedIds, setLikedIds] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [feedback, setFeedback] = useState('');

  const toggleLike = (id: number) => {
    setLikedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const publish = (event: FormEvent) => {
    event.preventDefault();
    const text = content.trim();
    if (!text) {
      setFeedback('写点什么再发布吧。');
      return;
    }
    const newComment: DiscussComment = {
      id: Date.now(),
      topicId,
      user: name.trim() || '匿名网友',
      role: '网友 · 本页发布',
      content: text,
      time: '刚刚',
      likes: 0,
      featured: false,
    };
    setComments((prev) => [newComment, ...prev]);
    setContent('');
    setFeedback('已发布，你的讨论已显示在本页列表顶部。');
  };

  return (
    <section id="discussion" className="scroll-mt-24 border-t border-background-200 bg-background-50">
      <div className="w-full px-4 py-12 md:px-8 md:py-14">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary-600">
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
              Discussion
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
              讨论区（{formatCount(comments.length)}）
            </h2>
          </div>
          <p className="max-w-md text-sm text-foreground-600">
            说说你的判断，也欢迎补充我们没找到的信息。请就事论事，不要涉及个人隐私。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <form onSubmit={publish} className="border border-background-200 bg-background-100 p-5">
              <label htmlFor={`nickname-${topicId}`} className="text-xs font-medium text-foreground-600">
                昵称（选填）
              </label>
              <input
                id={`nickname-${topicId}`}
                name="nickname"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="随便取个名字"
                maxLength={20}
                className="mt-2 h-10 w-full border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />

              <label htmlFor={`content-${topicId}`} className="mt-4 block text-xs font-medium text-foreground-600">
                你的看法
              </label>
              <textarea
                id={`content-${topicId}`}
                name="content"
                value={content}
                onChange={(e) => setContent(e.target.value.slice(0, MAX_LENGTH))}
                rows={4}
                placeholder={`围绕「${topicTitle}」说说你的判断`}
                className="mt-2 w-full resize-none border border-background-300 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-foreground-400">
                  {content.length}/{MAX_LENGTH}
                </span>
                <div className="flex items-center gap-3">
                  {feedback && <span className="text-xs text-primary-600">{feedback}</span>}
                  <button
                    type="submit"
                    className="whitespace-nowrap bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    发布讨论
                  </button>
                </div>
              </div>
            </form>

            <ul className="mt-6 flex flex-col gap-4">
              {comments.map((comment) => {
                const liked = likedIds.includes(comment.id);
                return (
                  <li key={comment.id} className="border border-background-200 bg-background-100 p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center bg-accent-100 font-heading text-sm font-bold text-accent-900">
                        {comment.user.slice(0, 1)}
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-foreground-900">{comment.user}</span>
                        <span className="block text-xs text-foreground-500">{comment.role}</span>
                      </span>
                      <span className="text-xs text-foreground-400">{comment.time}</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
                    <div className="mt-3 border-t border-background-200 pt-3">
                      <button
                        type="button"
                        onClick={() => toggleLike(comment.id)}
                        aria-pressed={liked}
                        className={`flex cursor-pointer items-center gap-1.5 text-xs transition-colors ${
                          liked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                        }`}
                      >
                        <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} aria-hidden="true" />
                        {formatCount(comment.likes + (liked ? 1 : 0))}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-shield-check-line text-secondary-600" aria-hidden="true" />
                讨论区公约
              </h3>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                  围绕事情本身讨论，不做人身评价
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                  不发布未经证实的个人信息
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                  引用信息时尽量附上公开来源
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                  允许被说服，也允许改变观点
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}